import type { SoftphoneProviderProps } from "@dialstack/sdk-react/softphone";
import { useCallback, useEffect, useRef, useState } from "react";

import { datadogRum } from "@datadog/browser-rum";

// Owns the DialStack session token for the softphone and keeps the phone
// alive across suno-backend hiccups (ENG-1966).
//
// The SDK refreshes its own token through `onTokenExpiring`, but a single
// failed refresh is terminal on its side: it emits `auth_failed`, never
// re-arms the timer, and the server drops the session when the old token
// expires. And once a session is gone for any non-terminal reason the SDK
// does not auto-reconnect — it reports `disconnected`/`error` and waits.
//
// So three things happen here: the first mint keeps retrying through a
// backend outage instead of giving up on load, `refreshToken` retries the
// mint before giving up, and whenever the connection lands in
// `disconnected`/`error` while we still want a phone, a fresh session is
// minted and handed to the SDK as a new `token`. `SoftphoneProvider` keys its
// phone on that prop — a new value disposes the dead phone and connects a
// new one.

type SoftphoneError = Parameters<
  NonNullable<SoftphoneProviderProps["onError"]>
>[0];
type SoftphoneConnectionEvent = Parameters<
  NonNullable<SoftphoneProviderProps["onConnectionStateChange"]>
>[0];

// Delays between attempts when `onTokenExpiring` fails. The SDK fires it
// 60s before expiry, so this must stay comfortably inside that window.
export const TOKEN_REFRESH_RETRY_DELAYS_MS = [5000, 15000, 30000];

// Delay before each re-mint after the connection dies, indexed by attempt.
// The last value repeats: a tab left open through a backend outage keeps
// knocking once a minute until the phone is back.
export const SESSION_RECOVERY_BACKOFF_MS = [2000, 5000, 15000, 30000, 60000];

// Errors after which a fresh session must NOT be minted: the server ended
// this session on purpose (another tab took the phone, an admin revoked it,
// too many sessions) or the user has to act first. Re-minting after
// `session_replaced` would fight the tab that just took over.
const TERMINAL_ERROR_CODES = new Set([
  "session_replaced",
  "session_revoked",
  "session_limit",
  "emergency_address_required",
  "mic_permission_denied"
]);

// Per-call errors already surface as a chip in the widget; only
// connection-level errors are worth an Error Tracking entry.
const CALL_LEVEL_ERROR_CODES = new Set(["call_failed", "call_not_found"]);

export const SOFTPHONE_RECOVERED_ACTION = "dialstack_softphone_recovered";

// A 4xx from the mint is the backend's verdict on this user — voice not
// provisioned (403), practice not activated (409) — and asking again will not
// change it. 401 is left out: a stray one no longer logs the user out (see
// baseQueryWithReauthFn), so it is retried like any other hiccup; if the Suno
// session really is dead the next ordinary request ends it and `enabled`
// flips off. Everything else (5xx, network, timeout, 408/429) is transient.
const isPermanentMintFailure = (error: unknown) => {
  const status = (error as { status?: unknown } | null)?.status;
  return (
    typeof status === "number" &&
    status >= 400 &&
    status < 500 &&
    status !== 401 &&
    status !== 408 &&
    status !== 429
  );
};

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

const backoffFor = (attempt: number) =>
  SESSION_RECOVERY_BACKOFF_MS[
    Math.min(attempt, SESSION_RECOVERY_BACKOFF_MS.length - 1)
  ];

type Options = {
  // False while the phone must not exist at all (voice off, no user, another
  // tab leads). Flipping it drops the token and cancels any pending work.
  enabled: boolean;
  // Whose session this is: a different user gets a fresh mint.
  userId: number | null;
  mint: () => Promise<string>;
};

export function useDialstackSessionToken({ enabled, userId, mint }: Options) {
  const [token, setToken] = useState<string | null>(null);
  // True while a usable phone is expected but not here yet: the first mint is
  // in flight or being retried, or the phone died and a new session is on its
  // way. Drives the widget's "Phone connecting…" stand-in.
  const [sessionPending, setSessionPending] = useState(false);
  // Bumped whenever `enabled` flips: any mint/retry/recovery loop that
  // started under an older generation drops its result on the floor.
  const generationRef = useRef(0);
  const recoveryTimerRef = useRef<number | null>(null);
  const recoveryAttemptRef = useRef(0);
  const lastErrorCodeRef = useRef<string | null>(null);
  // Set once a terminal code is seen for the current phone; a later
  // `transport_closed` or `disconnected` must not undo that verdict.
  const sawTerminalErrorRef = useRef(false);

  const clearRecoveryTimer = useCallback(() => {
    if (recoveryTimerRef.current !== null) {
      window.clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
  }, []);

  const scheduleRecovery = useCallback(() => {
    if (recoveryTimerRef.current !== null) return;
    const generation = generationRef.current;
    const attempt = recoveryAttemptRef.current;
    recoveryAttemptRef.current = attempt + 1;
    setSessionPending(true);
    recoveryTimerRef.current = window.setTimeout(() => {
      recoveryTimerRef.current = null;
      if (generationRef.current !== generation) return;
      mint()
        .then((clientSecret) => {
          if (generationRef.current !== generation) return;
          datadogRum.addAction(SOFTPHONE_RECOVERED_ACTION, {
            attempts: attempt + 1,
            lastErrorCode: lastErrorCodeRef.current
          });
          setToken(clientSecret);
          setSessionPending(false);
        })
        .catch((error: unknown) => {
          if (generationRef.current !== generation) return;
          if (isPermanentMintFailure(error)) setSessionPending(false);
          else scheduleRecovery();
        });
    }, backoffFor(attempt));
  }, [mint]);

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    clearRecoveryTimer();
    recoveryAttemptRef.current = 0;
    lastErrorCodeRef.current = null;
    sawTerminalErrorRef.current = false;
    if (!enabled) {
      setToken(null);
      setSessionPending(false);
      return;
    }
    setSessionPending(true);
    mint()
      .then((clientSecret) => {
        if (generationRef.current !== generation) return;
        setToken(clientSecret);
        setSessionPending(false);
      })
      .catch((error: unknown) => {
        if (generationRef.current !== generation) return;
        // Voice not provisioned for this user/practice: the softphone stays
        // unavailable and the websocket screen-pop snackbar remains the
        // fallback. Anything else is a hiccup worth retrying — a page loaded
        // during a backend blip must still end up with a phone.
        if (isPermanentMintFailure(error)) setSessionPending(false);
        else scheduleRecovery();
      });
  }, [clearRecoveryTimer, enabled, mint, scheduleRecovery, userId]);

  useEffect(() => clearRecoveryTimer, [clearRecoveryTimer]);

  // `onTokenExpiring`: the SDK calls this once, ~60s before expiry, and treats
  // a rejection as final — so the retrying happens here.
  const refreshToken = useCallback(async () => {
    const generation = generationRef.current;
    for (let attempt = 0; ; attempt += 1) {
      try {
        return await mint();
      } catch (error) {
        const delay = TOKEN_REFRESH_RETRY_DELAYS_MS[attempt];
        if (delay === undefined || generationRef.current !== generation)
          throw error;
        await wait(delay);
        if (generationRef.current !== generation)
          throw new Error("Softphone session token refresh cancelled");
      }
    }
  }, [mint]);

  const handleError = useCallback(({ code, message }: SoftphoneError) => {
    lastErrorCodeRef.current = code;
    if (TERMINAL_ERROR_CODES.has(code)) sawTerminalErrorRef.current = true;
    if (CALL_LEVEL_ERROR_CODES.has(code)) return;
    datadogRum.addError(new Error(`DialStack softphone ${code}: ${message}`), {
      feature: "dialstack-softphone",
      code
    });
  }, []);

  const handleConnectionStateChange = useCallback(
    ({ state }: SoftphoneConnectionEvent) => {
      if (state === "connected") {
        clearRecoveryTimer();
        recoveryAttemptRef.current = 0;
        lastErrorCodeRef.current = null;
        sawTerminalErrorRef.current = false;
        setSessionPending(false);
        return;
      }
      if (state !== "disconnected" && state !== "error") return;
      if (sawTerminalErrorRef.current) return;
      scheduleRecovery();
    },
    [clearRecoveryTimer, scheduleRecovery]
  );

  return {
    token,
    sessionPending,
    refreshToken,
    handleError,
    handleConnectionStateChange
  };
}
